<template>
  <div class="app-layout">
    <Sidebar />
    <div class="users-container">
      <div class="header">
        <h1>Users Management</h1>
        <div class="header-actions">
          <div class="search-container">
            <input 
              type="text" 
              v-model="searchQuery" 
              @input="handleSearch" 
              placeholder="Search users..."
              class="search-input"
            />
          </div>
          <div class="buttons-container">
            <button @click="exportUsers" class="export-button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export Users
            </button>
            <router-link to="/users/create" class="create-button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Create User
            </router-link>
          </div>
        </div>
      </div>

      <div class="statistics-grid">
            <div class="stat-card">
              <div class="stat-icon total-users">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div class="stat-info">
                <h3>Total Users</h3>
                <p>{{ statistics.total_users }}</p>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon active-users">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="8.5" cy="7" r="4"></circle>
                  <path d="M20 8l2 2l-2 2"></path>
                  <path d="M15 9h7"></path>
                </svg>
              </div>
              <div class="stat-info">
                <h3>Active Users</h3>
                <p>{{ statistics.active_users }}</p>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon new-users">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="8.5" cy="7" r="4"></circle>
                  <line x1="20" y1="8" x2="20" y2="14"></line>
                  <line x1="23" y1="11" x2="17" y2="11"></line>
                </svg>
              </div>
              <div class="stat-info">
                <h3>New This Month</h3>
                <p>{{ statistics.users_this_month }}</p>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon admins">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="8.5" cy="7" r="4"></circle>
                  <path d="M20 7h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3H16"></path>
                  <path d="M18 14v-4"></path>
                </svg>
              </div>
              <div class="stat-info">
                <h3>Admins</h3>
                <p>{{ statistics.total_admins }}</p>
              </div>
            </div>
          </div>

      <!-- Add Filter Section -->
      <div class="filters-section">
        <div class="filters-header">
          <h3>Filters</h3>
          <button @click="resetFilters" class="reset-filters-btn">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="filter-icon">
              <path d="M19 4v-.5A.5.5 0 0018.5 3h-13a.5.5 0 00-.5.5V4l5 5v7l4 4v-11l5-5z"/>
            </svg>
            Reset Filters
          </button>
        </div>
        
        <div class="filters-grid">
          <div class="filter-group">
            <label>Activity Status</label>
            <select v-model="filters.activity_status" @change="applyFilters">
              <option value="">All Statuses</option>
              <option v-for="status in activityStatusOptions" :key="status" :value="status">
                {{ status.charAt(0).toUpperCase() + status.slice(1) }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label>User Type</label>
            <select v-model="filters.user_type" @change="applyFilters">
              <option value="">All Types</option>
              <option v-for="type in userTypeOptions" :key="type" :value="type">
                {{ type.charAt(0).toUpperCase() + type.slice(1) }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label>Account Age</label>
            <select v-model="filters.account_age" @change="applyFilters">
              <option value="">Any Age</option>
              <option v-for="age in accountAgeOptions" :key="age" :value="age">
                {{ age === 'all' ? 'All' : formatAccountAge(age) }}
              </option>
            </select>
          </div>

          <div class="filter-group date-filter">
            <label>Date Range</label>
            <div class="date-range">
              <div class="date-input-wrapper">
                <input 
                  type="date" 
                  v-model="filters.date_from"
                  @change="applyFilters"
                  class="date-input"
                  :max="filters.date_to"
                />
                <label class="date-label">From</label>
              </div>
              <div class="date-input-wrapper">
                <input 
                  type="date" 
                  v-model="filters.date_to"
                  @change="applyFilters"
                  class="date-input"
                  :min="filters.date_from"
                />
                <label class="date-label">To</label>
              </div>
            </div>
          </div>

          <div class="filter-group">
            <label>Verification Status</label>
            <select v-model="filters.verification_status" @change="applyFilters">
              <option value="">All Verification</option>
              <option v-for="status in verificationStatusOptions" :key="status" :value="status">
                {{ status.charAt(0).toUpperCase() + status.slice(1) }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label>Assessment Status</label>
            <select v-model="filters.assessment_status" @change="applyFilters">
              <option value="">All Assessment</option>
              <option v-for="status in assessmentStatusOptions" :key="status" :value="status">
                {{ status.charAt(0).toUpperCase() + status.slice(1) }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label>Profile Picture</label>
            <div class="toggle-group">
              <label class="toggle-button" :class="{ active: filters.has_profile_picture === 'true' }">
                <input 
                  type="radio" 
                  v-model="filters.has_profile_picture"
                  @change="applyFilters"
                  value="true"
                  class="hidden"
                >
                Has Picture
              </label>
              <label class="toggle-button" :class="{ active: filters.has_profile_picture === 'false' }">
                <input 
                  type="radio" 
                  v-model="filters.has_profile_picture"
                  @change="applyFilters"
                  value="false"
                  class="hidden"
                >
                No Picture
              </label>
              <label class="toggle-button" :class="{ active: filters.has_profile_picture === '' }">
                <input 
                  type="radio" 
                  v-model="filters.has_profile_picture"
                  @change="applyFilters"
                  value=""
                  class="hidden"
                >
                All
              </label>
            </div>
          </div>

          <div class="filter-group">
            <label>Phone Number</label>
            <div class="toggle-group">
              <label class="toggle-button" :class="{ active: filters.has_phone === 'true' }">
                <input 
                  type="radio" 
                  v-model="filters.has_phone"
                  @change="applyFilters"
                  value="true"
                  class="hidden"
                >
                Has Phone
              </label>
              <label class="toggle-button" :class="{ active: filters.has_phone === 'false' }">
                <input 
                  type="radio" 
                  v-model="filters.has_phone"
                  @change="applyFilters"
                  value="false"
                  class="hidden"
                >
                No Phone
              </label>
              <label class="toggle-button" :class="{ active: filters.has_phone === '' }">
                <input 
                  type="radio" 
                  v-model="filters.has_phone"
                  @change="applyFilters"
                  value=""
                  class="hidden"
                >
                All
              </label>
            </div>
          </div>

          <div class="filter-group">
            <label>Account Status</label>
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input 
                  type="checkbox" 
                  v-model="filters.is_blocked" 
                  @change="applyFilters"
                  :true-value="'false'"
                  :false-value="''"
                  class="custom-checkbox"
                >
                <span class="checkbox-text">Show Unblocked</span>
              </label>
              <label class="checkbox-label">
                <input 
                  type="checkbox" 
                  v-model="filters.is_deleted" 
                  @change="applyFilters"
                  :true-value="'false'"
                  :false-value="''"
                  class="custom-checkbox"
                >
                <span class="checkbox-text">Show Active</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="content">
        <div v-if="loading" class="loading">
          <div class="spinner"></div>
          <p>Loading users...</p>
        </div>

        <div v-else-if="error" class="error">
          <p>{{ error }}</p>
          <button @click="fetchUsers" class="retry-button">Retry</button>
        </div>

        <div v-else>

          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th @click="sortBy('id')" class="sortable">
                    ID
                    <span class="sort-icon" :class="{ active: sortField === 'id' }">
                      {{ sortField === 'id' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                    </span>
                  </th>
                  <th @click="sortBy('first_name')" class="sortable">
                    Name
                    <span class="sort-icon" :class="{ active: sortField === 'first_name' }">
                      {{ sortField === 'first_name' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                    </span>
                  </th>
                  <th @click="sortBy('email')" class="sortable">
                    Email
                    <span class="sort-icon" :class="{ active: sortField === 'email' }">
                      {{ sortField === 'email' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                    </span>
                  </th>
                  <th @click="sortBy('is_active')" class="sortable">
                    Status
                    <span class="sort-icon" :class="{ active: sortField === 'is_active' }">
                      {{ sortField === 'is_active' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                    </span>
                  </th>
                  <th @click="sortBy('last_login_at')" class="sortable">
                    Last Login
                    <span class="sort-icon" :class="{ active: sortField === 'last_login_at' }">
                      {{ sortField === 'last_login_at' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}
                    </span>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in users" :key="user.id">
                  <td>{{ user.id }}</td>
                  <td>
                    <div class="user-info">
                      <div v-if="user.img_url" class="user-avatar">
                        <img :src="user.img_url" :alt="user.first_name" />
                      </div>
                      <div v-else class="user-avatar placeholder">
                        {{ getInitials(user.first_name, user.last_name) }}
                      </div>
                      <span>{{ formatName(user.first_name, user.last_name) }}</span>
                    </div>
                  </td>
                  <td>{{ user.email }}</td>
                  <td>
                    <span :class="['status-badge', getStatusClass(user)]">
                      {{ getStatusText(user) }}
                    </span>
                  </td>
                  <td>{{ formatDate(user.last_login_at) }}</td>
                  <td>
                    <div class="actions">
                      <button class="action-btn edit" @click="editUser(user)">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button class="action-btn delete" @click="deleteUser(user)">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M3 6h18"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pagination">
            <button 
              :disabled="currentPage === 1" 
              @click="changePage(currentPage - 1)"
              class="pagination-btn"
            >
              Previous
            </button>
            
            <div class="page-numbers">
              <button 
                v-for="page in displayedPages" 
                :key="page"
                :class="['page-number', { active: page === currentPage }]"
                @click="changePage(page)"
              >
                {{ page }}
              </button>
            </div>

            <button 
              :disabled="currentPage === totalPages" 
              @click="changePage(currentPage + 1)"
              class="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- User Details Modal -->
    <div v-if="showUserModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h2>User Details</h2>
          <button @click="closeUserModal" class="close-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div v-if="selectedUser" class="modal-body">
          <div class="user-profile">
            <div class="user-avatar-large">
              <img v-if="selectedUser.img_url" :src="selectedUser.img_url" :alt="selectedUser.first_name" />
              <div v-else class="placeholder">
                {{ getInitials(selectedUser.first_name, selectedUser.last_name) }}
              </div>
            </div>
            <div class="user-info-large">
              <h3>{{ formatName(selectedUser.first_name, selectedUser.last_name) }}</h3>
              <p class="email">{{ selectedUser.email }}</p>
              <div class="status-actions">
                <span :class="['status-badge', getStatusClass(selectedUser)]">
                  {{ getStatusText(selectedUser) }}
                </span>
                <div class="action-buttons">
                  <button 
                    @click="toggleUserStatus(selectedUser)" 
                    class="action-btn toggle-status"
                    :disabled="loading"
                  >
                    {{ selectedUser.is_active ? 'Deactivate' : 'Activate' }}
                  </button>
                  <button 
                    v-if="!selectedUser.is_deleted"
                    @click="toggleUserBlock(selectedUser)" 
                    :class="['action-btn', selectedUser.is_blocked ? 'unblock' : 'block']"
                    :disabled="loading"
                  >
                    {{ selectedUser.is_blocked ? 'Unblock' : 'Block' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="details-grid">
            <div class="detail-item">
              <label>Phone Number</label>
              <p>{{ selectedUser.phone_number || 'Not provided' }}</p>
            </div>
            <div class="detail-item">
              <label>Date of Birth</label>
              <p>{{ selectedUser.date_of_birth ? new Date(selectedUser.date_of_birth).toLocaleDateString() : 'Not provided' }}</p>
            </div>
            <div class="detail-item">
              <label>Gender</label>
              <p>{{ selectedUser.gender ? selectedUser.gender.charAt(0).toUpperCase() + selectedUser.gender.slice(1) : 'Not provided' }}</p>
            </div>
            <div class="detail-item">
              <label>Last Login</label>
              <p>{{ formatDate(selectedUser.last_login_at) }}</p>
            </div>
            <div class="detail-item">
              <label>Account Created</label>
              <p>{{ formatDate(selectedUser.created_at) }}</p>
            </div>
            <div class="detail-item">
              <label>Email Verified</label>
              <p>{{ selectedUser.email_verified_at ? formatDate(selectedUser.email_verified_at) : 'Not verified' }}</p>
            </div>
          </div>

          <div class="subscriptions-section">
            <h4>Subscriptions</h4>
            <div v-if="selectedUser.subscriptions && selectedUser.subscriptions.length" class="subscriptions-list">
              <div v-for="sub in selectedUser.subscriptions" :key="sub.id" class="subscription-item">
                <div class="subscription-info">
                  <span class="platform">{{ sub.platform }}</span>
                  <span :class="['status', sub.status]">{{ sub.status }}</span>
                </div>
                <div class="subscription-dates">
                  <span>{{ new Date(sub.start_date).toLocaleDateString() }} - {{ new Date(sub.end_date).toLocaleDateString() }}</span>
                </div>
              </div>
            </div>
            <p v-else>No active subscriptions</p>
          </div>

          <div class="purchases-section">
            <h4>Recent Purchases</h4>
            <div v-if="selectedUser.purchases && selectedUser.purchases.length" class="purchases-list">
              <div v-for="purchase in selectedUser.purchases.slice(0, 5)" :key="purchase.id" class="purchase-item">
                <div class="purchase-info">
                  <span class="item-type">{{ purchase.item_type }}</span>
                  <span class="amount">${{ purchase.amount }}</span>
                </div>
                <div class="purchase-date">
                  {{ new Date(purchase.purchase_date).toLocaleDateString() }}
                </div>
              </div>
            </div>
            <p v-else>No purchase history</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay">
      <div class="modal-content delete-modal">
        <div class="modal-header">
          <h2>Delete User</h2>
          <button @click="closeDeleteModal" class="close-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div v-if="userToDelete" class="modal-body">
          <div class="delete-warning">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
            <p>Are you sure you want to delete this user?</p>
          </div>
          
          <div class="user-to-delete-info">
            <p class="name">{{ formatName(userToDelete.first_name, userToDelete.last_name) }}</p>
            <p class="email">{{ userToDelete.email }}</p>
          </div>

          <div class="delete-actions">
            <button @click="closeDeleteModal" class="cancel-button">
              Cancel
            </button>
            <button @click="confirmDelete" class="delete-button" :disabled="loading">
              <span v-if="loading">Deleting...</span>
              <span v-else>Delete User</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import debounce from 'lodash/debounce';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'UsersManagement',
  components: {
    Sidebar
  },
  setup() {
    const users = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const searchQuery = ref('');
    const selectedUser = ref(null);
    const showUserModal = ref(false);
    const showDeleteModal = ref(false);
    const userToDelete = ref(null);
    const statistics = ref({
      total_users: 0,
      total_admins: 0,
      users_this_month: 0,
      active_users: 0
    });
    const sortField = ref('id');
    const sortDirection = ref('asc');

    const filters = ref({
      per_page: 10,
      activity_status: '',
      user_type: '',
      account_age: '',
      date_from: '',
      date_to: '',
      verification_status: '',
      assessment_status: '',
      has_profile_picture: '',
      has_phone: '',
      is_blocked: '',
      is_deleted: ''
    });

    const activityStatusOptions = [ 'active', 'inactive'];
    const userTypeOptions = [ 'admin', 'user'];
    const accountAgeOptions = [ '1month', '3months', '6months', '1year'];
    const verificationStatusOptions = [ 'verified', 'unverified'];
    const assessmentStatusOptions = [ 'completed', 'pending', 'failed'];

    const fetchUsers = async (page = 1, search = '') => {
      loading.value = true;
      error.value = null;

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Map frontend sort fields to backend field names
        const sortFieldMapping = {
          'first_name': 'first_name',
          'email': 'email',
          'is_active': 'is_active',
          'last_login_at': 'last_login_at',
          'id': 'id'
        };

        const params = {
          page,
          search,
          per_page: filters.value.per_page,
          sort_by: sortFieldMapping[sortField.value] || 'id',
          sort_direction: sortDirection.value
        };

        // Add filters only if they have values
        if (filters.value.activity_status && filters.value.activity_status !== 'all') {
          params.activity_status = filters.value.activity_status;
        }
        if (filters.value.user_type && filters.value.user_type !== 'all') {
          params.user_type = filters.value.user_type;
        }
        if (filters.value.account_age && filters.value.account_age !== 'all') {
          params.account_age = filters.value.account_age;
        }
        if (filters.value.date_from) {
          params.date_from = filters.value.date_from;
        }
        if (filters.value.date_to) {
          params.date_to = filters.value.date_to;
        }
        if (filters.value.verification_status && filters.value.verification_status !== 'all') {
          params.verification_status = filters.value.verification_status;
        }
        if (filters.value.assessment_status && filters.value.assessment_status !== 'all') {
          params.assessment_status = filters.value.assessment_status;
        }
        if (filters.value.has_profile_picture !== '') {
          params.has_profile_picture = filters.value.has_profile_picture;
        }
        if (filters.value.has_phone !== '') {
          params.has_phone = filters.value.has_phone;
        }
        if (filters.value.is_blocked !== '') {
          params.is_blocked = filters.value.is_blocked;
        }
        if (filters.value.is_deleted !== '') {
          params.is_deleted = filters.value.is_deleted;
        }

        console.log('Fetching users with params:', params);

        const response = await axios.get(`https://api.kewaar.com/api/admin/users`, {
          params,
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          users.value = response.data.data.data;
          currentPage.value = response.data.data.current_page;
          totalPages.value = response.data.data.last_page;
          console.log('Users fetched successfully:', response.data);
        } else {
          throw new Error(response.data.message || 'Failed to fetch users');
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        error.value = err.message || 'An error occurred while fetching users';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = debounce(() => {
      currentPage.value = 1;
      fetchUsers(1, searchQuery.value);
    }, 300);

    const changePage = (page) => {
      fetchUsers(page, searchQuery.value);
    };

    const getInitials = (firstName, lastName) => {
      const first = firstName ? firstName.charAt(0).toUpperCase() : '';
      const last = lastName ? lastName.charAt(0).toUpperCase() : '';
      return first + last || '??';
    };

    const formatName = (firstName, lastName) => {
      if (!firstName && !lastName) return 'Unknown User';
      return `${firstName || ''} ${lastName || ''}`.trim();
    };

    const formatDate = (date) => {
      if (!date) return 'Never';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const getStatusClass = (user) => {
      if (!user.is_active) return 'inactive';
      if (user.is_blocked) return 'blocked';
      if (user.is_deleted) return 'deleted';
      return 'active';
    };

    const getStatusText = (user) => {
      if (!user.is_active) return 'Inactive';
      if (user.is_blocked) return 'Blocked';
      if (user.is_deleted) return 'Deleted';
      return 'Active';
    };

    const editUser = async (user) => {
      loading.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.get(`https://api.kewaar.com/api/admin/users/${user.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          selectedUser.value = response.data.data;
          showUserModal.value = true;
        } else {
          throw new Error(response.data.message || 'Failed to fetch user details');
        }
      } catch (err) {
        error.value = err.message || 'An error occurred while fetching user details';
      } finally {
        loading.value = false;
      }
    };

    const closeUserModal = () => {
      showUserModal.value = false;
      selectedUser.value = null;
    };

    const deleteUser = (user) => {
      userToDelete.value = user;
      showDeleteModal.value = true;
    };

    const confirmDelete = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.delete(`https://api.kewaar.com/api/admin/users/${userToDelete.value.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          // Remove user from the list
          users.value = users.value.filter(u => u.id !== userToDelete.value.id);
          closeDeleteModal();
        } else {
          throw new Error(response.data.message || 'Failed to delete user');
        }
      } catch (err) {
        error.value = err.message || 'An error occurred while deleting user';
      } finally {
        loading.value = false;
      }
    };

    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      userToDelete.value = null;
    };

    const displayedPages = computed(() => {
      const pages = [];
      const maxPages = 5;
      const halfMax = Math.floor(maxPages / 2);
      
      let start = Math.max(currentPage.value - halfMax, 1);
      let end = Math.min(start + maxPages - 1, totalPages.value);
      
      if (end - start + 1 < maxPages) {
        start = Math.max(end - maxPages + 1, 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    });

    const toggleUserStatus = async (user) => {
      loading.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.put(
          `https://api.kewaar.com/api/admin/users/${user.id}/toggle-status`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          // Update user in the list and selected user
          const updatedUser = response.data.data;
          users.value = users.value.map(u => u.id === user.id ? updatedUser : u);
          selectedUser.value = updatedUser;
        } else {
          throw new Error(response.data.message || 'Failed to toggle user status');
        }
      } catch (err) {
        error.value = err.message || 'An error occurred while toggling user status';
      } finally {
        loading.value = false;
      }
    };

    const toggleUserBlock = async (user) => {
      loading.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const endpoint = user.is_blocked ? 'unblock' : 'block';
        const response = await axios.put(
          `https://api.kewaar.com/api/admin/users/${user.id}/${endpoint}`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          // Update user in the list and selected user
          const updatedUser = response.data.data;
          users.value = users.value.map(u => u.id === user.id ? updatedUser : u);
          selectedUser.value = updatedUser;
        } else {
          throw new Error(response.data.message || `Failed to ${endpoint} user`);
        }
      } catch (err) {
        error.value = err.message || `An error occurred while ${user.is_blocked ? 'unblocking' : 'blocking'} user`;
      } finally {
        loading.value = false;
      }
    };

    const fetchStatistics = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.get('https://api.kewaar.com/api/admin/users/statistics/overview', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          statistics.value = response.data.data;
        } else {
          throw new Error(response.data.message || 'Failed to fetch statistics');
        }
      } catch (err) {
        console.error('Error fetching statistics:', err);
      }
    };

    const sortBy = (field) => {
      if (sortField.value === field) {
        // Toggle direction if clicking the same field
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
      } else {
        // New field, default to ascending
        sortField.value = field;
        sortDirection.value = 'asc';
      }
      
      // Refetch users with new sorting
      fetchUsers(currentPage.value, searchQuery.value);
    };

    const applyFilters = () => {
      currentPage.value = 1;
      fetchUsers(1, searchQuery.value);
    };

    const resetFilters = () => {
      filters.value = {
        per_page: 10,
        activity_status: '',
        user_type: '',
        account_age: '',
        date_from: '',
        date_to: '',
        verification_status: '',
        assessment_status: '',
        has_profile_picture: '',
        has_phone: '',
        is_blocked: '',
        is_deleted: ''
      };
      applyFilters();
    };

    const formatAccountAge = (age) => {
      const mapping = {
        '1month': '1 Month',
        '3months': '3 Months',
        '6months': '6 Months',
        '1year': '1 Year'
      };
      return mapping[age] || age;
    };

    const exportUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          alert('No authentication token found. Please log in again.');
          return;
        }
        
        // Show loading indication or notification if you have one
        loading.value = true;
        
        // Create URL with any active filters
        let url = 'https://api.kewaar.com/api/admin/users/export';
        let params = new URLSearchParams();
        
        // Add current filters to the export
        if (filters.value.activity_status) params.append('activity_status', filters.value.activity_status);
        if (filters.value.user_type) params.append('user_type', filters.value.user_type);
        if (filters.value.account_age) params.append('account_age', filters.value.account_age);
        if (filters.value.date_from) params.append('date_from', filters.value.date_from);
        if (filters.value.date_to) params.append('date_to', filters.value.date_to);
        if (filters.value.verification_status) params.append('verification_status', filters.value.verification_status);
        if (filters.value.assessment_status) params.append('assessment_status', filters.value.assessment_status);
        if (filters.value.has_profile_picture) params.append('has_profile_picture', filters.value.has_profile_picture);
        if (filters.value.has_phone) params.append('has_phone', filters.value.has_phone);
        if (filters.value.is_blocked) params.append('is_blocked', filters.value.is_blocked);
        if (filters.value.is_deleted) params.append('is_deleted', filters.value.is_deleted);
        if (searchQuery.value) params.append('search', searchQuery.value);
        
        // Format: csv or xlsx
        params.append('format', 'xlsx');
        
        const finalUrl = `${url}?${params.toString()}`;
        
        // Option 1: Direct download via window.open (simpler approach)
        window.open(finalUrl + `&token=${token}`, '_blank');
        
        /* 
        // Option 2: Ajax request and handle blob (more control but more complex)
        const response = await axios.get(finalUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          responseType: 'blob'
        });
        
        // Create a download link for the blob
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        */
        
        // Optional: Show success message
        alert('Export started. Your file will download shortly.');
      } catch (error) {
        console.error('Error exporting users:', error);
        alert('Failed to export users. Please try again.');
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchUsers();
      fetchStatistics();
    });

    return {
      users,
      loading,
      error,
      currentPage,
      totalPages,
      searchQuery,
      displayedPages,
      selectedUser,
      showUserModal,
      showDeleteModal,
      userToDelete,
      handleSearch,
      changePage,
      getInitials,
      formatName,
      formatDate,
      getStatusClass,
      getStatusText,
      editUser,
      deleteUser,
      confirmDelete,
      closeDeleteModal,
      fetchUsers,
      closeUserModal,
      toggleUserStatus,
      toggleUserBlock,
      statistics,
      sortField,
      sortDirection,
      sortBy,
      filters,
      applyFilters,
      resetFilters,
      activityStatusOptions,
      userTypeOptions,
      accountAgeOptions,
      verificationStatusOptions,
      assessmentStatusOptions,
      formatAccountAge,
      exportUsers
    };
  }
};
</script>

<style scoped>
.app-layout {
  display: flex;
  min-height: 100vh;
}

.users-container {
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
}

@media (max-width: 768px) {
  .users-container {
    margin-left: 0;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.search-container {
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading {
  padding: 4rem;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #4f46e5;
  border-radius: 50%;
  margin: 0 auto 1rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error {
  padding: 2rem;
  text-align: center;
  color: #dc2626;
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background: #b91c1c;
}

.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th {
  background: #f8fafc;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
}

td {
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
  color: #1a1a1a;
  font-size: 0.875rem;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  overflow: hidden;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-avatar.placeholder {
  background: #4f46e5;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.75rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge.active {
  background: #dcfce7;
  color: #166534;
}

.status-badge.inactive {
  background: #f1f5f9;
  color: #475569;
}

.status-badge.blocked {
  background: #fee2e2;
  color: #991b1b;
}

.status-badge.deleted {
  background: #fef3c7;
  color: #92400e;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.action-btn svg {
  width: 16px;
  height: 16px;
}

.action-btn.edit {
  color: #4f46e5;
}

.action-btn.edit:hover {
  background: rgba(79, 70, 229, 0.1);
}

.action-btn.delete {
  color: #dc2626;
}

.action-btn.delete:hover {
  background: rgba(220, 38, 38, 0.1);
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  gap: 1rem;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:not(:disabled):hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
}

.page-number {
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
}

.page-number:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.page-number.active {
  background: #4f46e5;
  border-color: #4f46e5;
  color: white;
}

.create-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #4f46e5;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
}

.create-button:hover {
  background: #4338ca;
}

.create-button svg {
  width: 16px;
  height: 16px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #1a1a1a;
}

.close-button {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #64748b;
  transition: color 0.2s;
}

.close-button:hover {
  color: #1a1a1a;
}

.close-button svg {
  width: 20px;
  height: 20px;
}

.modal-body {
  padding: 1.5rem;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.user-avatar-large {
  width: 96px;
  height: 96px;
  border-radius: 12px;
  overflow: hidden;
}

.user-avatar-large img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-avatar-large .placeholder {
  width: 100%;
  height: 100%;
  background: #4f46e5;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 600;
}

.user-info-large h3 {
  margin: 0 0 0.5rem;
  font-size: 1.5rem;
  color: #1a1a1a;
}

.user-info-large .email {
  margin: 0 0 0.75rem;
  color: #64748b;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.detail-item {
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
}

.detail-item label {
  display: block;
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.detail-item p {
  margin: 0;
  color: #1a1a1a;
  font-weight: 500;
}

.subscriptions-section,
.purchases-section {
  margin-top: 2rem;
}

.subscriptions-section h4,
.purchases-section h4 {
  margin: 0 0 1rem;
  color: #1a1a1a;
  font-size: 1.125rem;
}

.subscriptions-list,
.purchases-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.subscription-item,
.purchase-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
}

.subscription-info,
.purchase-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.platform,
.item-type {
  text-transform: capitalize;
  font-weight: 500;
}

.status {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status.active {
  background: #dcfce7;
  color: #166534;
}

.status.expired {
  background: #fee2e2;
  color: #991b1b;
}

.amount {
  font-weight: 600;
  color: #1a1a1a;
}

.subscription-dates,
.purchase-date {
  color: #64748b;
  font-size: 0.875rem;
}

.delete-modal {
  max-width: 500px;
}

.delete-warning {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 1.5rem;
}

.delete-warning svg {
  width: 48px;
  height: 48px;
  color: #dc2626;
  margin-bottom: 1rem;
}

.delete-warning p {
  font-size: 1.125rem;
  color: #1a1a1a;
  margin: 0;
}

.user-to-delete-info {
  text-align: center;
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.user-to-delete-info .name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.25rem;
}

.user-to-delete-info .email {
  color: #64748b;
  margin: 0;
}

.delete-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.cancel-button {
  padding: 0.75rem 1.5rem;
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-button:hover {
  background: #e2e8f0;
}

.delete-button {
  padding: 0.75rem 1.5rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.delete-button:hover:not(:disabled) {
  background: #b91c1c;
}

.delete-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.status-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.toggle-status {
  background: #4f46e5;
  color: white;
}

.toggle-status:hover:not(:disabled) {
  background: #4338ca;
}

.block {
  background: #dc2626;
  color: white;
}

.block:hover:not(:disabled) {
  background: #b91c1c;
}

.unblock {
  background: #16a34a;
  color: white;
}

.unblock:hover:not(:disabled) {
  background: #166534;
}

.statistics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon svg {
  width: 24px;
  height: 24px;
}

.stat-icon.total-users {
  background: rgba(79, 70, 229, 0.1);
  color: #4f46e5;
}

.stat-icon.active-users {
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
}

.stat-icon.new-users {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.stat-icon.admins {
  background: rgba(219, 39, 119, 0.1);
  color: #db2777;
}

.stat-info {
  flex: 1;
}

.stat-info h3 {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.stat-info p {
  margin: 0.25rem 0 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
}

.sortable {
  cursor: pointer;
  user-select: none;
  position: relative;
  padding-right: 2rem;
  transition: background-color 0.2s;
}

.sortable:hover {
  background: #f1f5f9;
}

.sort-icon {
  position: absolute;
  right: 0.75rem;
  color: #94a3b8;
  font-size: 0.875rem;
}

.sort-icon.active {
  color: #4f46e5;
}

/* Updated Filter Styles */
.filters-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.filters-header h3 {
  font-size: 1.25rem;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.reset-filters-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  color: #64748b;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
}

.reset-filters-btn:hover {
  background: #e2e8f0;
  color: #475569;
}

.filter-icon {
  width: 16px;
  height: 16px;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.filter-group select {
  padding: 0.625rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1a1a1a;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-group select:hover {
  border-color: #cbd5e1;
}

.filter-group select:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.date-filter {
  grid-column: span 2;
}

.date-range {
  display: flex;
  gap: 1rem;
}

.date-input-wrapper {
  flex: 1;
  position: relative;
}

.date-input {
  width: 100%;
  padding: 0.625rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1a1a1a;
  transition: all 0.2s;
}

.date-input:hover {
  border-color: #cbd5e1;
}

.date-input:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.date-label {
  position: absolute;
  top: -0.5rem;
  left: 0.5rem;
  padding: 0 0.25rem;
  background: white;
  font-size: 0.75rem;
  color: #64748b;
}

.toggle-group {
  display: flex;
  gap: 0.5rem;
}

.toggle-button {
  flex: 1;
  padding: 0.5rem;
  text-align: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-button:hover {
  background: #f1f5f9;
}

.toggle-button.active {
  background: #4f46e5;
  border-color: #4f46e5;
  color: white;
}

.hidden {
  display: none;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.custom-checkbox {
  appearance: none;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
}

.custom-checkbox:checked {
  background: #4f46e5;
  border-color: #4f46e5;
}

.custom-checkbox:checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-text {
  font-size: 0.875rem;
  color: #1a1a1a;
}

.buttons-container {
  display: flex;
  gap: 1rem;
}

.export-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #10B981;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
  cursor: pointer;
}

.export-button:hover {
  background: #059669;
}

.export-button svg {
  width: 16px;
  height: 16px;
}

@media (max-width: 768px) {
  .header-actions {
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }
  
  .search-container {
    width: 100%;
  }
  
  .buttons-container {
    width: 100%;
  }
  
  .create-button,
  .export-button {
    flex: 1;
    justify-content: center;
  }
}
</style> 